import { useEffect, useRef, useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";

const PRESET_COLORS = [
  "#ef4444", "#f59e0b", "#eab308", "#10b981", "#0d9488",
  "#0ea5e9", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
  "#ec4899", "#f43f5e", "#64748b", "#a16207",
];

const MAX_CODE_LENGTH = 40;

// =====================================================================
// CustomFindingDialog — small modal for adding a clinic-custom finding.
// Wired by the parent (ChartingTab) to upsert_chart_finding.
// Visible only to clinic_admin / doctor (chart.manage_findings).
// =====================================================================
export default function CustomFindingDialog({
  busy = false,
  onSave,        // ({ code, display_name, color_hex, requires_surfaces }) => void
  onClose,
}) {
  const [name, setName]       = useState("");
  const [color, setColor]     = useState(PRESET_COLORS[7]);
  const [scope, setScope]     = useState("surface"); // 'surface' | 'tooth'
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  // Auto-derive code: lowercase, replace whitespace, restrict to [a-z0-9_].
  const code = name.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  const codeTooLong = code.length > MAX_CODE_LENGTH;
  const canSave = !busy && name.trim().length > 0 && code.length > 0 && !codeTooLong;

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true"
      data-testid="custom-finding-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 bg-teal-50">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 text-sm">Add custom finding</p>
            <p className="text-xs text-teal-700">Visible only to your clinic.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="custom-finding-close"
            className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Name
            </label>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Watch on #18"
              maxLength={64}
              data-testid="custom-finding-name"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
            <p className="text-[11px] text-slate-400">
              Internal code: <code className="bg-slate-100 px-1 rounded break-all">{code || "—"}</code>
            </p>
            {codeTooLong ? (
              <p className="text-[11px] font-medium text-rose-600" data-testid="custom-finding-code-error">
                Use 40 characters or fewer after spaces become underscores.
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Color
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="custom-finding-colors">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  data-testid={`custom-finding-color-${c.replace("#","")}`}
                  className={`w-7 h-7 rounded-full ring-1 ring-black/10 transition-transform ${
                    color === c ? "ring-2 ring-slate-900 scale-110" : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Scope
            </label>
            <div className="grid grid-cols-2 gap-2" data-testid="custom-finding-scope">
              <button
                type="button"
                onClick={() => setScope("surface")}
                data-testid="custom-finding-scope-surface"
                className={`px-3 py-2 rounded-xl border text-sm font-medium ${
                  scope === "surface"
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
              >
                Surface
              </button>
              <button
                type="button"
                onClick={() => setScope("tooth")}
                data-testid="custom-finding-scope-tooth"
                className={`px-3 py-2 rounded-xl border text-sm font-medium ${
                  scope === "tooth"
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
              >
                Whole tooth
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              Surface = applies to M/O/D/B/L. Whole tooth = lights up the entire flower.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="custom-finding-cancel"
              className="px-4 py-2.5 text-sm border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canSave}
              onClick={() => onSave?.({
                code,
                display_name: name.trim(),
                color_hex: color,
                requires_surfaces: scope === "surface",
              })}
              data-testid="custom-finding-save"
              className="px-5 py-2.5 text-sm font-semibold bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {busy ? "Saving…" : "Save finding"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
